import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import { API_BASE } from '../lib/api';

// ─── Sprint ONBOARD ONB-FIX.D9 (#1081) — Reset-password page ───────────────
//
// Public route /reset-password?token=<raw>. Submits new password to
// POST /auth/reset-password. On success, redirects to /login per Q12
// (no auto-login — user re-authenticates with the new credentials).
//
// Status mapping (from backend):
//   200 → ok: redirect to /login with ?reset=success
//   400 → Zod boundary (password too short/long, missing field) — show inline
//   410 → INVALID_OR_EXPIRED_TOKEN — show "link expired" state with a
//         link back to /forgot-password to request a fresh one
//   422 → service-layer WEAK_PASSWORD (defence-in-depth; rare in practice)
//
// ⚠️ Q10 single-state on 410: the page collapses never-existed / expired /
// already-used into ONE error message ("Your reset link has expired or
// has already been used"). The backend does not distinguish these
// sub-cases (S1 — no enumeration via error messaging), and the UI
// must not invent a distinction either.
//
// Token handling: parsed from `?token=...` query param. Held in a
// component-state variable, never logged to console, never written to
// localStorage. The variable goes out of scope on navigation, which is
// JS-engine-level memory hygiene (we can't explicitly zero a string in
// JS, but we avoid keeping references beyond the form submit).

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};

function BlueprintMark({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="10" fill="#7C3AED" />
      <path d="M12 32 L24 26 L36 32 L24 38 Z" fill="white" opacity="0.22" />
      <path d="M12 26 L24 20 L36 26 L24 32 Z" fill="white" opacity="0.55" />
      <path d="M12 20 L24 14 L36 20 L24 26 Z" fill="white" opacity="0.9" />
      <circle cx="36" cy="12" r="5" fill="#EC4899" />
    </svg>
  );
}

// Mirror the backend Zod constraint exactly. Frontend validation is UX-only
// per S6 — the backend re-validates regardless.
const MIN_PASSWORD = 8;
const MAX_PASSWORD = 72;

type PageState =
  | { kind: 'no-token' }
  | { kind: 'form'; error?: string }
  | { kind: 'submitting' }
  | { kind: 'expired-or-invalid' }
  | { kind: 'success' };

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Snapshot the token in component state at mount so we still have it
  // available after history.replaceState strips it from the URL below.
  // useSearchParams() reads location.search live, so without the snapshot
  // it would return '' after the replaceState fires.
  const [token] = useState(() => searchParams.get('token') ?? '');

  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState]                     = useState<PageState>(
    token ? { kind: 'form' } : { kind: 'no-token' },
  );

  // ── R1 CRITICAL-2 (adversarial 2026-05-25) — strip the raw token from
  // the URL immediately on mount. Without this, the token persists:
  //   - in window.location.href (visible to any script — analytics,
  //     third-party widgets, browser extensions)
  //   - in the browser's HTTP Referer header on every outbound request
  //     (third-party fonts, the <a href="/login"> back-link below, etc.)
  //   - in browser history (visible via Back button, shoulder-surfing,
  //     persistent across the session)
  //
  // history.replaceState removes the token from the URL bar without
  // navigating. The component still has the token via the useState
  // snapshot above. Mirrors the high-security pattern from Google /
  // GitHub / Okta password-reset flows. Runs once on mount only.
  useEffect(() => {
    if (token) {
      window.history.replaceState({}, '', '/reset-password');
    }
    // Intentional empty deps — fires once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On success, redirect to /login after a short delay so the user sees
  // the confirmation. Q12 — no auto-login; user re-enters credentials.
  useEffect(() => {
    if (state.kind === 'success') {
      const t = setTimeout(() => navigate('/login?reset=success'), 1500);
      return () => clearTimeout(t);
    }
  }, [state.kind, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.kind !== 'form') return;

    // Client-side validations — UX only (backend re-validates per S6).
    if (password.length < MIN_PASSWORD || password.length > MAX_PASSWORD) {
      setState({ kind: 'form', error: `Password must be ${MIN_PASSWORD}-${MAX_PASSWORD} characters.` });
      return;
    }
    if (password !== confirmPassword) {
      setState({ kind: 'form', error: 'Passwords do not match.' });
      return;
    }

    setState({ kind: 'submitting' });
    try {
      // R1 NIT-2 review: ResetPasswordPage intentionally retains raw
      // fetch instead of switching to apiFetch like its sibling. Reason:
      // we MUST discriminate 200 vs 410 vs 422 vs 400 at this layer
      // (different UX state for each), but apiFetch throws on every
      // non-2xx with the body's `error` string only — destroying the
      // status code that drives the discriminator below. Migrating
      // would require either string-matching error messages (brittle)
      // or extending apiFetch with a status-passthrough mode (scope
      // creep for D-9). The raw fetch here is deliberate and matches
      // the EmailVerificationPage pattern for the same reason.
      //
      // ⚠️ Codex P1 (post-CI 2026-05-25): the URL MUST be prefixed with
      // API_BASE — a bare `/api/auth/reset-password` resolves to the
      // web-app origin (app.blueprintsdlc.com) in production. The SPA
      // static fallback returns index.html with status 200, this page
      // would treat that as success, and the user would believe their
      // password was reset when it wasn't. Same failure class as the
      // hotfix #1097 LoginPage apiFetch bug. Defense-in-depth: also
      // verify the response body shape (`ok: true`) so a future static
      // fallback returning text/html doesn't slip past the status check.
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, newPassword: password }),
      });

      if (res.status === 200) {
        // Defense-in-depth body-shape check (per Codex P1 fix). The API
        // returns { ok: true } on success; an HTML SPA-fallback body
        // parsed as JSON throws, so the catch below collapses the
        // false-success into a generic error state.
        const body = await res.json().catch(() => null) as { ok?: boolean } | null;
        if (body && body.ok === true) {
          setState({ kind: 'success' });
          return;
        }
        setState({ kind: 'form', error: 'Something went wrong. Please try again.' });
        return;
      }
      if (res.status === 410) {
        setState({ kind: 'expired-or-invalid' });
        return;
      }
      if (res.status === 422 || res.status === 400) {
        const body = await res.json().catch(() => ({}));
        const msg  = (body && typeof body.error === 'string')
          ? body.error
          : 'That password isn’t acceptable. Try a different one.';
        setState({ kind: 'form', error: msg });
        return;
      }
      // Unexpected status — generic copy, no 5xx-detail leak.
      setState({ kind: 'form', error: 'Something went wrong. Please try again.' });
    } catch {
      setState({ kind: 'form', error: 'Network error. Please check your connection and try again.' });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <BlueprintMark size={48} />
          <div className="flex items-baseline mt-3">
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Blue</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.5px' }}>Print</span>
          </div>
        </div>

        {state.kind === 'no-token' && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '0 0 8px' }}>
              Invalid reset link
            </h1>
            <p
              data-testid="reset-password-no-token"
              style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.55, margin: '0 0 20px' }}
            >
              This page can only be reached from a password reset email.
              Request a new link below.
            </p>
            <Link
              to="/forgot-password"
              style={{
                display:       'block',
                width:         '100%',
                height:        44,
                lineHeight:    '44px',
                textAlign:     'center',
                background:    'var(--primary)',
                color:         'white',
                fontFamily:    'var(--font-display)',
                fontSize:      15,
                fontWeight:    600,
                borderRadius:  'var(--radius-sm)',
                textDecoration: 'none',
              }}
            >
              Request a new link
            </Link>
          </>
        )}

        {state.kind === 'expired-or-invalid' && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '0 0 8px' }}>
              This link has expired
            </h1>
            <p
              data-testid="reset-password-expired"
              style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.55, margin: '0 0 20px' }}
            >
              Your reset link has expired or has already been used.
              Request a new one to continue.
            </p>
            <Link
              to="/forgot-password"
              style={{
                display:       'block',
                width:         '100%',
                height:        44,
                lineHeight:    '44px',
                textAlign:     'center',
                background:    'var(--primary)',
                color:         'white',
                fontFamily:    'var(--font-display)',
                fontSize:      15,
                fontWeight:    600,
                borderRadius:  'var(--radius-sm)',
                textDecoration: 'none',
              }}
            >
              Request a new link
            </Link>
          </>
        )}

        {state.kind === 'success' && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '0 0 8px' }}>
              Password updated
            </h1>
            <p
              data-testid="reset-password-success"
              style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.55, margin: '0 0 8px' }}
            >
              Your password has been changed. Redirecting you to sign in&hellip;
            </p>
          </>
        )}

        {(state.kind === 'form' || state.kind === 'submitting') && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '0 0 8px' }}>
              Set a new password
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.5 }}>
              Choose a password you haven&rsquo;t used before. {MIN_PASSWORD}&ndash;{MAX_PASSWORD} characters.
            </p>

            <form onSubmit={handleSubmit}>
              {state.kind === 'form' && state.error && (
                <div
                  role="alert"
                  data-testid="reset-password-error"
                  style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}
                >
                  {state.error}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="new-password" style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                  New password
                </label>
                <PasswordInput
                  id="new-password"
                  required
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                  maxLength={MAX_PASSWORD}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={inputStyle}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label htmlFor="confirm-password" style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                  Confirm new password
                </label>
                <PasswordInput
                  id="confirm-password"
                  required
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                  maxLength={MAX_PASSWORD}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  style={inputStyle}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>

              <button
                type="submit"
                disabled={state.kind === 'submitting'}
                style={{
                  display: 'block',
                  width: '100%',
                  height: 44,
                  background: 'var(--primary)',
                  color: 'white',
                  fontFamily: 'var(--font-display)',
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  cursor: state.kind === 'submitting' ? 'not-allowed' : 'pointer',
                  opacity: state.kind === 'submitting' ? 0.7 : 1,
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { if (state.kind !== 'submitting') e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {state.kind === 'submitting' ? 'Updating…' : 'Set new password'}
              </button>
            </form>
          </>
        )}

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 24 }}>
          <Link
            to="/login"
            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            &larr; Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
